"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import {
  Bell,
  BriefcaseBusiness,
  CheckCircle2,
  MessageSquare,
  Star,
  Megaphone,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useNotifications } from "@/hooks/use-notifications";
import { BottomNav } from "@/components/bottom-nav";
import { createClient } from "@/lib/supabase/client";
import type { NotificationType } from "@/lib/types";

const typeConfig: Record<NotificationType, { icon: typeof Bell; color: string; label: string }> = {
  match: { icon: BriefcaseBusiness, color: "text-blue-600 bg-blue-100", label: "Match" },
  hire_request: { icon: BriefcaseBusiness, color: "text-orange-600 bg-orange-100", label: "Hire Request" },
  hire_confirmed: { icon: CheckCircle2, color: "text-green-600 bg-green-100", label: "Hired!" },
  rating: { icon: Star, color: "text-yellow-600 bg-yellow-100", label: "Rating" },
  job_posted: { icon: Megaphone, color: "text-purple-600 bg-purple-100", label: "New Job" },
  system: { icon: MessageSquare, color: "text-gray-600 bg-gray-100", label: "System" },
};

export default function NotificationsPage() {
  const router = useRouter();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications({
    limit: 50,
  });
  const [userType, setUserType] = useState<"contractor" | "worker">("worker");

  useEffect(() => {
    async function fetchUserType() {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("type")
          .eq("id", user.id)
          .single();
        if (profile?.type) {
          setUserType(profile.type as "contractor" | "worker");
        }
      }
    }
    fetchUserType();
  }, []);

  function handleNotificationClick(notification: (typeof notifications)[0]) {
    markAsRead(notification.id);

    // Deep link based on notification data
    const data = notification.data as Record<string, string> | null;
    if (data?.conversationId) {
      router.push(`/messages/${data.conversationId}`);
    } else if (notification.type === "hire_confirmed" && (data?.matchId || data?.match_id)) {
      // Worker hired → show job details + contractor contact
      router.push(`/worker/hired/${data.matchId || data.match_id}`);
    } else if (notification.type === "hire_confirmed") {
      // Fallback if no match ID in data
      router.push("/worker/jobs");
    } else if (notification.type === "match" && data?.jobId) {
      // Contractor notified of match → go to matches page
      router.push("/contractor/matches");
    } else if (notification.type === "match") {
      router.push("/dashboard");
    } else if (notification.type === "job_posted") {
      router.push("/worker/jobs");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="mx-auto max-w-screen-sm px-4 py-6 pb-24">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Notifications</h1>
          </div>
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
        <BottomNav userType={userType} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="mx-auto max-w-screen-sm px-4 py-6 pb-24">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">Notifications</h1>
            {unreadCount > 0 && (
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-2 text-xs font-bold text-primary-foreground">
                {unreadCount}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllAsRead}>
              Mark all read
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-4">
              <Bell className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-lg font-medium">No notifications yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              You&apos;ll see updates about jobs, matches, and messages here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => {
              const config = typeConfig[notification.type];
              const Icon = config.icon;

              return (
                <Card
                  key={notification.id}
                  className={cn(
                    "cursor-pointer transition-colors hover:bg-accent/50 active:scale-[0.99]",
                    !notification.read && "border-primary/30 bg-primary/5"
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className="flex items-start gap-3 p-4">
                    <div
                      className={cn(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                        config.color
                      )}
                      title={config.label}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            "text-sm",
                            !notification.read && "font-semibold"
                          )}
                        >
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                        )}
                      </div>
                      {notification.body && (
                        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav userType={userType} />
    </div>
  );
}
